import { Suspense } from 'react'
import { useQuery } from '@tanstack/react-query'
import { portalApi } from '@/features/portal/api'
import { qk } from '@/api/query-keys'
import { OfficeLiveProvider } from '@/features/office'
import { Navigate, Outlet, createBrowserRouter, useLocation, useParams } from 'react-router'
import { AppShell } from '@/components/layout/app-shell'
import { BrandLoader, PageLoading } from '@/components/composites/states'
import { AbilityProvider } from '@/features/permissions/AbilityProvider'
import { WorkspaceProvider, useWorkspace } from '@/features/workspace/WorkspaceProvider'
import { useAuth } from '@/features/auth/AuthProvider'
import { LoginPage } from '@/features/auth/LoginPage'
import { NoAccessPage } from '@/features/auth/NoAccessPage'
import { getErrorMessage, isApiError } from '@/api/errors'
import { paths } from './paths'
import { RouteError } from './RouteError'
import { lazyWithReload } from './lazyWithReload'
import { LegacyFrozen } from '@/features/support-desk/LegacyFrozen'

// Every page is lazy; the shell and auth are not.
const WorkDashboardPage = lazyWithReload('WorkDashboardPage', () =>
  import('@/features/work/WorkDashboardPage').then((m) => ({ default: m.WorkDashboardPage })),
)
const GmailHandoffPage = lazyWithReload('GmailHandoffPage', () =>
  import('@/features/extension/GmailHandoffPage').then((m) => ({ default: m.GmailHandoffPage })),
)
const ConnectPage = lazyWithReload('ConnectPage', () =>
  import('@/features/extension/ConnectPage').then((m) => ({ default: m.ConnectPage })),
)
const MeetingsPage = lazyWithReload('CalendarPage', () =>
  import('@/features/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })),
)
const DraftsPage = lazyWithReload('DraftsPage', () =>
  import('@/features/drafts/DraftsPage').then((m) => ({ default: m.DraftsPage })),
)
const ArchivePage = lazyWithReload('ArchivePage', () =>
  import('@/features/archive/ArchivePage').then((m) => ({ default: m.ArchivePage })),
)
const OverduePage = lazyWithReload('OverduePage', () =>
  import('@/features/etas/OverduePage').then((m) => ({ default: m.OverduePage })),
)
const ApprovalsPage = lazyWithReload('ApprovalsPage', () =>
  import('@/features/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
)
const RequestsPage = lazyWithReload('RequestsPage', () =>
  import('@/features/requests/RequestsPage').then((m) => ({ default: m.RequestsPage })),
)
const SupportTicketsPage = lazyWithReload('SupportTicketsPage', () =>
  import('@/features/support-tickets/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage })),
)
const SupportDeskPage = lazyWithReload('SupportDeskPage', () =>
  import('@/features/support-desk/SupportDeskPage').then((m) => ({ default: m.SupportDeskPage })),
)
const SupportTicketPage = lazyWithReload('SupportTicketPage', () =>
  import('@/features/support-desk/SupportTicketPage').then((m) => ({ default: m.SupportTicketPage })),
)
const SupportDashboardPage = lazyWithReload('SupportDashboardPage', () =>
  import('@/features/support-desk/SupportDashboardPage').then((m) => ({ default: m.SupportDashboardPage })),
)
const SupportTicketQueuePage = lazyWithReload('SupportTicketQueuePage', () =>
  import('@/features/support-tickets/SupportTicketQueuePage').then((m) => ({ default: m.SupportTicketQueuePage })),
)
const ReportingPage = lazyWithReload('ReportingPage', () =>
  import('@/features/reporting/ReportingPage').then((m) => ({ default: m.ReportingPage })),
)
const ReportsPage = lazyWithReload('ReportsPage', () =>
  import('@/features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
)
const SupportReportPage = lazyWithReload('SupportReportPage', () =>
  import('@/features/reports/support/SupportReportPage').then((m) => ({ default: m.SupportReportPage })),
)
const PilotsPage = lazyWithReload('PilotsPage', () =>
  import('@/features/pilots/PilotsPage').then((m) => ({ default: m.PilotsPage })),
)
const CrmPage = lazyWithReload('CrmPage', () =>
  import('@/features/crm').then((m) => ({ default: m.CrmPage })),
)
const ScribbleArea = lazyWithReload('ScribbleArea', () =>
  import('@/features/scribble').then((m) => ({ default: m.ScribbleArea })),
)
const ScribblePage = lazyWithReload('ScribblePage', () =>
  import('@/features/scribble').then((m) => ({ default: m.ScribblePage })),
)
const DiaryPage = lazyWithReload('DiaryPage', () =>
  import('@/features/diary').then((m) => ({ default: m.DiaryPage })),
)
const TeamDayPage = lazyWithReload('TeamDayPage', () =>
  import('@/features/office').then((m) => ({ default: m.TeamDayPage })),
)
const OfficePage = lazyWithReload('OfficePage', () =>
  import('@/features/office').then((m) => ({ default: m.OfficePage })),
)
const CommsPage = lazyWithReload('CommsPage', () =>
  import('@/features/comms').then((m) => ({ default: m.CommsPage })),
)
const HrPage = lazyWithReload('HrPage', () =>
  import('@/features/hr').then((m) => ({ default: m.HrPage })),
)
const ProcurementPage = lazyWithReload('ProcurementPage', () =>
  import('@/features/procurement').then((m) => ({ default: m.ProcurementPage })),
)
const ProductPage = lazyWithReload('ProductPage', () =>
  import('@/features/product').then((m) => ({ default: m.ProductPage })),
)
const GhlCompaniesPage = lazyWithReload('GhlCompaniesPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.CompaniesPage })),
)
const GhlCompanyPage = lazyWithReload('GhlCompanyPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.CompanyPage })),
)
const GhlBoardPage = lazyWithReload('GhlBoardPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.BoardPage })),
)
const GhlDashboardPage = lazyWithReload('GhlDashboardPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.DashboardPage })),
)
const GhlConversationsPage = lazyWithReload('GhlConversationsPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.ConversationsPage })),
)
const GhlContactsPage = lazyWithReload('GhlContactsPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.ContactsPage })),
)
const GhlContactPage = lazyWithReload('GhlContactPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.ContactPage })),
)
const GhlTasksPage = lazyWithReload('GhlTasksPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.TasksPage })),
)
const GhlMeetingsPage = lazyWithReload('GhlMeetingsPage', () =>
  import('@/features/crm-ghl').then((m) => ({ default: m.MeetingsPage })),
)
const FinancePage = lazyWithReload('FinancePage', () =>
  import('@/features/finance/FinancePage').then((m) => ({ default: m.FinancePage })),
)
const ClientsPage = lazyWithReload('ClientsPage', () =>
  import('@/features/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })),
)
const ProjectsPage = lazyWithReload('ProjectsPage', () =>
  import('@/features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
)
const ProjectPage = lazyWithReload('ProjectPage', () =>
  import('@/features/projects/ProjectPage').then((m) => ({ default: m.ProjectPage })),
)
const ClientProjectPage = lazyWithReload('ClientProjectPage', () =>
  import('@/features/client-projects/ClientProjectPage').then((m) => ({ default: m.ClientProjectPage })),
)
const ClientProjectPresentPage = lazyWithReload('ClientProjectPresentPage', () =>
  import('@/features/client-projects/ClientProjectPage').then((m) => ({ default: m.ClientProjectPresentPage })),
)
const OverviewPage = lazyWithReload('OverviewPage', () =>
  import('@/features/overview/OverviewPage').then((m) => ({ default: m.OverviewPage })),
)
const InsightsPage = lazyWithReload('InsightsPage', () =>
  import('@/features/insights/InsightsPage').then((m) => ({ default: m.InsightsPage })),
)
const PeoplePage = lazyWithReload('PeoplePage', () =>
  import('@/features/people/PeoplePage').then((m) => ({ default: m.PeoplePage })),
)
const PersonPage = lazyWithReload('PersonPage', () =>
  import('@/features/people/PersonPage').then((m) => ({ default: m.PersonPage })),
)
const TeamsPage = lazyWithReload('TeamsPage', () =>
  import('@/features/teams/TeamsPage').then((m) => ({ default: m.TeamsPage })),
)
const TeamPage = lazyWithReload('TeamPage', () =>
  import('@/features/teams/TeamPage').then((m) => ({ default: m.TeamPage })),
)
const RolesPage = lazyWithReload('RolesPage', () =>
  import('@/features/settings/RolesPage').then((m) => ({ default: m.RolesPage })),
)
const MembersPage = lazyWithReload('MembersPage', () =>
  import('@/features/settings/MembersPage').then((m) => ({ default: m.MembersPage })),
)
const AuditPage = lazyWithReload('AuditPage', () =>
  import('@/features/settings/AuditPage').then((m) => ({ default: m.AuditPage })),
)
const IntegrationsPage = lazyWithReload('IntegrationsPage', () =>
  import('@/features/settings/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
)
const SupportDeskSettingsPage = lazyWithReload('SupportDeskSettingsPage', () =>
  import('@/features/settings/SupportDeskSettingsPage').then((m) => ({ default: m.SupportDeskSettingsPage })),
)
const SupportTeamsPage = lazyWithReload('SupportTeamsPage', () =>
  import('@/features/settings/SupportTeamsPage').then((m) => ({ default: m.SupportTeamsPage })),
)
const NotificationsPage = lazyWithReload('NotificationsPage', () =>
  import('@/features/settings/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
)
const ClientPreviewSettingsPage = lazyWithReload('ClientPreviewSettingsPage', () =>
  import('@/features/settings/PreviewPages').then((m) => ({ default: m.ClientPreviewSettingsPage })),
)
const MemberPreviewSettingsPage = lazyWithReload('MemberPreviewSettingsPage', () =>
  import('@/features/settings/PreviewPages').then((m) => ({ default: m.MemberPreviewSettingsPage })),
)
const ClientPortalPreviewLayout = lazyWithReload('ClientPortalPreviewLayout', () =>
  import('@/features/client-preview/ClientPortalPreviewLayout').then((m) => ({ default: m.ClientPortalPreviewLayout })),
)
const KitchenSinkPage = lazyWithReload('KitchenSinkPage', () =>
  import('@/features/kitchen-sink/KitchenSinkPage').then((m) => ({ default: m.KitchenSinkPage })),
)

/**
 * The client portal — a separate surface, lazily loaded as its own chunk.
 *
 * Nothing internal is imported by these four modules, so none of the app shell, the ability
 * provider or the board ships to a customer's browser.
 */
const PortalLayout = lazyWithReload('PortalLayout', () =>
  import('@/features/portal/PortalLayout').then((m) => ({ default: m.PortalLayout })),
)
const PortalRequestsPage = lazyWithReload('PortalRequestsPage', () =>
  import('@/features/portal/PortalRequestsPage').then((m) => ({ default: m.PortalRequestsPage })),
)
const PortalItemsPage = lazyWithReload('PortalItemsPage', () =>
  import('@/features/portal/PortalItemsPage').then((m) => ({ default: m.PortalItemsPage })),
)
const PortalItemPage = lazyWithReload('PortalItemPage', () =>
  import('@/features/portal/PortalItemPage').then((m) => ({ default: m.PortalItemPage })),
)

/** The default workspace until the switcher is wired to GET /workspaces. */
const DEFAULT_WORKSPACE = 'product'

/**
 * Turns a failed ability fetch into something actionable.
 *
 * These three cases look identical to a user ("I can't get in") but need completely
 * different fixes, so collapsing them into one generic message just costs debugging time.
 */
function explainAccessError(error: unknown): string {
  const code = isApiError(error) ? error.code : undefined

  switch (code) {
    case 'ORG_NOT_FOUND':
      return 'There is no workspace set up for your email domain yet. Ask an admin to set one up.'
    case 'NOT_A_MEMBER':
      return 'Your account is not a member of this workspace yet. Ask an admin to invite you.'
    case 'MEMBER_DISABLED':
      return 'Your access has been disabled. Contact a workspace admin.'
    case 'AUTH_DOMAIN_NOT_ALLOWED':
      return 'This account is not set up for Tapwork. If you are a client, ask your contact to add your email domain to your portal.'
    case 'NETWORK':
    case 'TIMEOUT':
      return 'We could not reach Tapwork just now. Check your connection and try again in a moment.'
    default:
      return getErrorMessage(error)
  }
}

function RequireAuth() {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <BrandLoader label="Signing you in…" />
  if (status === 'signed-out') {
    // A pasted/deep Work Item link must survive authentication. The previous guard sent the
    // user to /login without remembering the requested pathname/query, and LoginPage then
    // sent them to '/', which is why ?card=... links landed on the plain main board.
    const returnTo = `${location.pathname}${location.search}${location.hash}`
    return (
      <Navigate
        to={`${paths.login()}?returnTo=${encodeURIComponent(returnTo)}`}
        replace
        state={{ returnTo }}
      />
    )
  }
  // Not one of ours — but very possibly a client. Their portal is the answer, not a refusal.
  if (status === 'external') return <ExternalRedirect />
  return <Outlet />
}

/**
 * A signed-in person on a non-internal email.
 *
 * They used to be shown "Tapwork is only available to @taptalent.ai accounts", which was
 * wrong twice over: it is not true — a client with a portal domain has access — and it made
 * the product look shut to exactly the people it was built to serve.
 *
 * `GET /portal/me` is the only thing that can answer it, because the mapping from an email
 * domain to a client lives in the database. It resolves, and they belong in the portal; it
 * does not, and they genuinely have no access, said without naming a domain.
 */
function ExternalRedirect() {
  const meQuery = useQuery({
    queryKey: qk.portalMe(),
    queryFn: portalApi.me,
    retry: false,
    staleTime: 5 * 60_000,
  })

  if (meQuery.isPending) return <BrandLoader label="Signing you in…" />
  if (meQuery.data) return <Navigate to={paths.portalRequests()} replace />

  return (
    <NoAccessPage reason="This account is not set up for Tapwork yet. If you are a client, ask your contact to add your email domain to your portal." />
  )
}

/**
 * Wraps the workspace subtree in the ability ruleset.
 *
 * Children never render before the ruleset resolves — a flash of content the user cannot
 * act on is worse than a moment of skeleton.
 */
function WorkspaceScope() {
  return (
    <WorkspaceProvider
      fallback={<BrandLoader label="Loading your workspace…" />}
      onMissing={(slug, available) => (
        <NoAccessPage
          reason={
            available.length === 0
              ? 'You are not a member of any workspace yet. Ask an admin to add you.'
              : `No workspace called "${slug}". Available: ${available.map((w) => w.slug).join(', ')}.`
          }
        />
      )}
    >
      <AbilityScope />
    </WorkspaceProvider>
  )
}

/** Separate component so it can read the RESOLVED workspace id, not the URL slug. */
function AbilityScope() {
  const { workspaceId } = useWorkspace()
  return (
    <AbilityProvider
      workspaceId={workspaceId}
      fallback={<BrandLoader label="Loading your access…" />}
      onError={(error) => <NoAccessPage reason={explainAccessError(error)} />}
    >
      <OfficeLiveProvider>
        <AppShell />
      </OfficeLiveProvider>
    </AbilityProvider>
  )
}

/** Old bookmarks (/my-work, /board, /boards/…) all land on the one dashboard. */
function RedirectToWork() {
  const { workspaceSlug = DEFAULT_WORKSPACE } = useParams()
  return <Navigate to={paths.work(workspaceSlug)} replace />
}

const suspense = (node: React.ReactNode) => (
  <Suspense fallback={<PageLoading />}>{node}</Suspense>
)

export const router = createBrowserRouter([
  { path: paths.login(), element: <LoginPage />, errorElement: <RouteError /> },
  { path: paths.noAccess(), element: <NoAccessPage /> },
  { path: paths.kitchenSink(), element: suspense(<KitchenSinkPage />) },

  /**
   * The client portal. Outside `RequireAuth` and outside `/w/:workspaceSlug`, both on purpose.
   *
   * `RequireAuth` sends a signed-in user whose email is not @taptalent.ai to /no-access — and
   * that describes every portal user, since access there is granted by the client's own
   * domain. `PortalLayout` carries its own guard, which accepts any signed-in account and
   * lets `GET /portal/me` decide whether that account belongs to a client.
   *
   * There is no `AbilityProvider` and no `WorkspaceProvider` in this subtree. An external
   * caller has neither, so mounting them would 403 before the first page rendered.
   */
  {
    errorElement: <RouteError />,
    path: paths.portal(),
    element: suspense(<PortalLayout />),
    children: [
      { index: true, element: <Navigate to="requests" replace /> },
      { path: 'requests', element: suspense(<PortalRequestsPage />) },
      { path: 'items', element: suspense(<PortalItemsPage />) },
      { path: 'items/:itemId', element: suspense(<PortalItemPage />) },
    ],
  },

  {
    errorElement: <RouteError />,
    element: <RequireAuth />,
    children: [
      { path: '/', element: <Navigate to={paths.office(DEFAULT_WORKSPACE)} replace /> },
      { path: paths.extensionGmail(), element: suspense(<GmailHandoffPage />) },
      { path: paths.extensionConnect(), element: suspense(<ConnectPage />) },
      {
        path: '/client-preview/:clientId',
        element: suspense(<ClientPortalPreviewLayout />),
        children: [
          { index: true, element: <Navigate to="requests" replace /> },
          { path: 'requests', element: suspense(<PortalRequestsPage />) },
          { path: 'items', element: suspense(<PortalItemsPage />) },
          { path: 'items/:itemId', element: suspense(<PortalItemPage />) },
        ],
      },
      {
        path: '/w/:workspaceSlug',
        element: <WorkspaceScope />,
        children: [
          { index: true, element: <Navigate to="office" replace /> },
          { path: 'work', element: suspense(<WorkDashboardPage />) },
          { path: 'drafts', element: suspense(<DraftsPage />) },
          { path: 'meetings', element: suspense(<MeetingsPage />) },
          { path: 'calendar', element: suspense(<MeetingsPage />) },
          { path: 'archive', element: suspense(<ArchivePage />) },
          { path: 'my-work', element: <RedirectToWork /> },
          { path: 'overdue', element: suspense(<OverduePage />) },
          { path: 'approvals', element: suspense(<ApprovalsPage />) },
          { path: 'requests', element: suspense(<RequestsPage />) },
          { path: 'support-tickets', element: suspense(<LegacyFrozen><SupportTicketsPage /></LegacyFrozen>) },
          { path: 'support-ticket', element: suspense(<LegacyFrozen><SupportTicketQueuePage /></LegacyFrozen>) },
          { path: 'support', element: suspense(<SupportDeskPage />) },
          { path: 'support/dashboard', element: suspense(<SupportDashboardPage />) },
          { path: 'support/ticket/:caseId', element: suspense(<SupportTicketPage />) },
          { path: 'pilots', element: suspense(<PilotsPage />) },
          { path: 'crm', element: suspense(<CrmPage />) },
          { path: 'crm/dashboard', element: suspense(<GhlDashboardPage />) },
          { path: 'crm/conversations', element: suspense(<GhlConversationsPage />) },
          { path: 'crm/contacts', element: suspense(<GhlContactsPage />) },
          { path: 'crm/contacts/:contactId', element: suspense(<GhlContactPage />) },
          { path: 'crm/accounts', element: suspense(<GhlCompaniesPage />) },
          { path: 'crm/accounts/:accountKey', element: suspense(<GhlCompanyPage />) },
          { path: 'crm/board', element: suspense(<GhlBoardPage />) },
          { path: 'crm/tasks', element: suspense(<GhlTasksPage />) },
          { path: 'crm/meetings', element: suspense(<GhlMeetingsPage />) },
          // Links shared before the rename keep working.
          { path: 'crm/companies', element: suspense(<GhlCompaniesPage />) },
          { path: 'crm/companies/:accountKey', element: suspense(<GhlCompanyPage />) },
          { path: 'crm/scorecard', element: suspense(<GhlDashboardPage />) },
          { path: 'product', element: suspense(<ProductPage />) },
          { path: 'procurement', element: suspense(<ProcurementPage />) },
          { path: 'hr', element: suspense(<HrPage />) },
          { path: 'comms', element: suspense(<CommsPage />) },
          { path: 'today', element: suspense(<TeamDayPage />) },
          { path: 'diary', element: suspense(<DiaryPage />) },
          { path: 'scribble', element: suspense(<ScribbleArea />) },
          { path: 'scribble/:id', element: suspense(<ScribblePage />) },
          { path: 'office', element: suspense(<OfficePage />) },
          { path: 'finance', element: suspense(<FinancePage />) },
          { path: 'reporting', element: suspense(<ReportingPage />) },
          { path: 'reports', element: suspense(<ReportsPage />) },
          { path: 'reports/support', element: suspense(<LegacyFrozen><SupportReportPage /></LegacyFrozen>) },
          { path: 'clients', element: suspense(<ClientsPage />) },
          { path: 'projects', element: suspense(<ProjectsPage />) },
          { path: 'projects/:projectId', element: suspense(<ProjectPage />) },
          { path: 'projects/:projectId/dashboard', element: suspense(<ClientProjectPage />) },
          { path: 'projects/:projectId/present', element: suspense(<ClientProjectPresentPage />) },
          // The old Timeline page is gone; each Client Project's dashboard carries its Gantt.
          { path: 'project-timeline', element: <Navigate to="../projects" replace /> },
          { path: 'overview', element: suspense(<OverviewPage />) },
          { path: 'insights', element: suspense(<InsightsPage />) },
          { path: 'people', element: suspense(<PeoplePage />) },
          { path: 'people/:memberId', element: suspense(<PersonPage />) },
          // One board per workspace, and it IS the Work dashboard.
          { path: 'board', element: <RedirectToWork /> },
          { path: 'boards/*', element: <RedirectToWork /> },
          { path: 'teams', element: suspense(<TeamsPage />) },
          { path: 'teams/:teamId', element: suspense(<TeamPage />) },
          { path: 'settings', element: <Navigate to="members" replace /> },
          { path: 'settings/members', element: suspense(<MembersPage />) },
          { path: 'settings/roles', element: suspense(<RolesPage />) },
          { path: 'settings/audit', element: suspense(<AuditPage />) },
          { path: 'settings/integrations', element: suspense(<IntegrationsPage />) },
          { path: 'settings/support-teams', element: suspense(<SupportTeamsPage />) },
          { path: 'settings/support-desk', element: suspense(<SupportDeskSettingsPage />) },
          { path: 'settings/notifications', element: suspense(<NotificationsPage />) },
          { path: 'settings/view-as-client', element: suspense(<ClientPreviewSettingsPage />) },
          { path: 'settings/view-as-member', element: suspense(<MemberPreviewSettingsPage />) },
        ],
      },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
])
